import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DeliverabilitySummaryResponse, GrowthCommsService, InteractionResponse, MessageTemplateResponse } from './growth-comms.service';
import {
  GROWTH_COMMS_PURPOSES,
  TemplateVariableForm,
  buildCommsChannelOptions,
  buildCommsPurposeOptions,
  createTemplateVariableRow,
  parseTemplateVariables,
  validateTemplateVariables
} from './growth-comms.models';

type CommsTab = 'interactions' | 'deliverability' | 'templates';

@Component({
  standalone: true,
  selector: 'ain-growth-comms-channels',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, DialogModule, InputTextModule,
    InputNumberModule, CheckboxModule, TabsModule, PartySelectorComponent,
    AinDatePipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.comms.channels.title')"
      [description]="t('growth.comms.channels.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.comms.channels.help.page.title'"
        [descriptionKey]="'growth.comms.channels.help.page.description'"
        [bulletKeys]="[
          'growth.comms.channels.help.page.bullets.action',
          'growth.comms.channels.help.page.bullets.sandbox',
          'growth.comms.channels.help.page.bullets.timeline',
          'growth.comms.channels.help.page.bullets.warning'
        ]" />

      <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
          <p-tab value="interactions">{{ t('growth.comms.channels.tabs.interactions') }}</p-tab>
          <p-tab value="deliverability">{{ t('growth.comms.channels.tabs.deliverability') }}</p-tab>
          <p-tab value="templates">{{ t('growth.comms.channels.tabs.templates') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="interactions">
            <section class="card">
              <div class="section-title">
                <h3>{{ t('growth.comms.interactions.sections.filters') }}</h3>
                <ain-info-hint
                  [titleKey]="'growth.comms.interactions.help.filters.title'"
                  [descriptionKey]="'growth.comms.interactions.help.filters.description'"
                  [bulletKeys]="[
                    'growth.comms.interactions.help.filters.bullets.how',
                    'growth.comms.interactions.help.filters.bullets.use'
                  ]" />
              </div>
              <div class="filter-grid">
                <ain-form-field [label]="t('growth.comms.interactions.filters.party')">
                  <ain-party-selector [(ngModel)]="filterPartyId" />
                </ain-form-field>
                <ain-form-field [label]="t('growth.comms.interactions.filters.channel')">
                  <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="filterChannel" [showClear]="true" appendTo="body" />
                </ain-form-field>
                <ain-form-field [label]="t('growth.comms.interactions.filters.status')">
                  <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="filterStatus" [showClear]="true" appendTo="body" />
                </ain-form-field>
              </div>
              <div class="toolbar">
                <button pButton type="button" [label]="t('catalog.action.search')" (click)="loadInteractions()"></button>
              </div>
            </section>
            @if (interactionsLoading()) {
              <ain-state type="loading" [title]="t('growth.comms.interactions.loading')" />
            } @else if (interactionsError()) {
              <ain-state type="error" [title]="t('growth.comms.interactions.errorTitle')" [message]="interactionsError() ?? ''" />
            } @else {
              <section class="card">
                <p-table [value]="interactions()" [paginator]="true" [rows]="15">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.comms.interactions.columns.channel') }}</th>
                      <th>{{ t('growth.comms.interactions.columns.purpose') }}</th>
                      <th>{{ t('growth.comms.interactions.columns.direction') }}</th>
                      <th>{{ t('growth.comms.interactions.columns.status') }}</th>
                      <th>{{ t('growth.comms.interactions.columns.provider') }}</th>
                      <th>{{ t('growth.comms.interactions.columns.occurredAt') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ t('growth.comms.channel.' + row.channelKey) }}</td>
                      <td>{{ t('growth.comms.purpose.' + row.purposeKey) }}</td>
                      <td>{{ row.direction }}</td>
                      <td>{{ row.status }}</td>
                      <td>{{ row.providerKey ?? '—' }}</td>
                      <td>{{ row.occurredAtUtc | ainDate:'datetime' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </section>
            }
          </p-tabpanel>

          <p-tabpanel value="deliverability">
            <section class="card">
              <ain-info-hint
                [titleKey]="'growth.comms.deliverability.help.title'"
                [descriptionKey]="'growth.comms.deliverability.help.description'"
                [bulletKeys]="[
                  'growth.comms.deliverability.help.bullets.how',
                  'growth.comms.deliverability.help.bullets.tenantScoped',
                  'growth.comms.deliverability.help.bullets.limit'
                ]" />
              <div class="summary-cards">
                @for (card of deliverabilityCards(); track card.key) {
                  <div class="summary-card">
                    <span class="label">{{ card.label }}</span>
                    <strong>{{ card.count }}</strong>
                  </div>
                }
              </div>
              <p-table [value]="deliverability()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.comms.deliverability.columns.channel') }}</th>
                    <th>{{ t('growth.comms.deliverability.columns.purpose') }}</th>
                    <th>{{ t('growth.comms.deliverability.columns.status') }}</th>
                    <th>{{ t('growth.comms.deliverability.columns.count') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t('growth.comms.channel.' + row.channelKey) }}</td>
                    <td>{{ t('growth.comms.purpose.' + row.purposeKey) }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.count }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>

          <p-tabpanel value="templates">
            <section class="card">
              <div class="section-title">
                <h3>{{ t('growth.comms.templates.sections.list') }}</h3>
                <ain-info-hint
                  [titleKey]="'growth.comms.templates.help.list.title'"
                  [descriptionKey]="'growth.comms.templates.help.list.description'"
                  [bulletKeys]="[
                    'growth.comms.templates.help.list.bullets.how',
                    'growth.comms.templates.help.list.bullets.eg4'
                  ]" />
              </div>
            <div class="toolbar toolbar-start">
              <button pButton type="button" [label]="t('growth.comms.templates.create')" (click)="openTemplateDialog()"></button>
            </div>
              <p-table [value]="templates()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('growth.comms.templates.columns.key') }}</th>
                    <th>{{ t('growth.comms.templates.columns.channel') }}</th>
                    <th>{{ t('growth.comms.templates.columns.purpose') }}</th>
                    <th>{{ t('growth.comms.templates.columns.status') }}</th>
                    <th>{{ t('growth.comms.templates.columns.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.templateKey }}</td>
                    <td>{{ t('growth.comms.channel.' + row.channelKey) }}</td>
                    <td>{{ t('growth.comms.purpose.' + row.purposeKey) }}</td>
                    <td>{{ row.status }}</td>
                    <td>
                      <button pButton type="button" severity="secondary" [label]="t('common.edit')" (click)="editTemplate(row)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>

      <p-dialog
        [(visible)]="templateDialogVisible"
        [header]="editingTemplateId ? t('growth.comms.templates.edit') : t('growth.comms.templates.create')"
        [modal]="true"
        [style]="{ width: '720px' }">
        <ain-info-hint
          [titleKey]="'growth.comms.templates.help.editor.title'"
          [descriptionKey]="'growth.comms.templates.help.editor.description'"
          [bulletKeys]="[
            'growth.comms.templates.help.editor.bullets.variables',
            'growth.comms.templates.help.editor.bullets.purpose',
            'growth.comms.templates.help.editor.bullets.afterSave'
          ]" />
        <div class="filter-grid">
          @if (!editingTemplateId) {
            <ain-form-field [label]="t('growth.comms.templates.fields.key')">
              <input pInputText [(ngModel)]="templateForm.templateKey" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.comms.templates.fields.channel')">
              <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="templateForm.channelKey" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.comms.templates.fields.purpose')">
              <p-select [options]="purposeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="templateForm.purposeKey" appendTo="body" />
            </ain-form-field>
          }
          <ain-form-field [label]="t('growth.comms.templates.fields.subject')">
            <input pInputText [(ngModel)]="templateForm.subjectTemplate" />
          </ain-form-field>
        </div>
        <ain-form-field [label]="t('growth.comms.templates.fields.body')">
          <textarea pInputTextarea [(ngModel)]="templateForm.bodyTemplate" rows="4" class="full-width"></textarea>
        </ain-form-field>
        <div class="section-title">
          <h4>{{ t('growth.comms.templates.sections.variables') }}</h4>
        </div>
        @for (row of templateVariables(); track row.localId) {
          <div class="filter-grid row-editor">
            <ain-form-field [label]="t('growth.comms.templates.fields.varKey')">
              <input pInputText [(ngModel)]="row.key" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.comms.templates.fields.varLabel')">
              <input pInputText [(ngModel)]="row.label" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.comms.templates.fields.required')">
              <p-checkbox [(ngModel)]="row.required" [binary]="true" />
            </ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeVariable(row.localId)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('growth.comms.templates.actions.addVariable')" (click)="addVariable()"></button>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="templateDialogVisible = false"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveTemplate()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .filter-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; }
    .row-editor { margin-bottom: 1rem; align-items: end; }
    .toolbar { display: flex; justify-content: flex-end; gap: .5rem; margin: 1rem 0; }
    .toolbar-start { justify-content: flex-start; }
    .section-title { margin-bottom: 1rem; }
    .card { background: var(--surface-card); border-radius: 8px; padding: 1rem; margin-bottom: 1rem; }
    .summary-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 1rem; margin: 1rem 0; }
    .summary-card { background: var(--surface-ground); border-radius: 8px; padding: 1rem; display: flex; flex-direction: column; gap: .25rem; }
    .full-width { width: 100%; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthCommsChannelsComponent implements OnInit {
  private readonly comms = inject(GrowthCommsService);
  private readonly i18n = inject(LocalizationService);

  activeTab = signal<CommsTab>('interactions');
  filterPartyId: string | null = null;
  filterChannel: string | null = null;
  filterStatus: string | null = null;
  interactions = signal<InteractionResponse[]>([]);
  interactionsLoading = signal(false);
  interactionsError = signal<string | null>(null);
  deliverability = signal<DeliverabilitySummaryResponse[]>([]);
  deliverabilityCards = signal<Array<{ key: string; label: string; count: number }>>([]);
  templates = signal<MessageTemplateResponse[]>([]);
  templateDialogVisible = false;
  editingTemplateId: string | null = null;
  templateForm = { templateKey: '', channelKey: 'email', purposeKey: 'marketing', subjectTemplate: '', bodyTemplate: '', status: 'Draft' };
  templateVariables = signal<TemplateVariableForm[]>([]);

  channelOptions = () => buildCommsChannelOptions(key => this.t(key));
  purposeOptions = () => buildCommsPurposeOptions(key => this.t(key));
  statusOptions = ['Queued', 'Sent', 'Delivered', 'Opened', 'Clicked', 'Failed', 'Bounced', 'Unsubscribed']
    .map(value => ({ label: value, value }));

  ngOnInit(): void {
    this.loadInteractions();
    this.loadDeliverability();
    this.loadTemplates();
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  onTabChange(value: string | number | undefined): void {
    if (value === 'interactions' || value === 'deliverability' || value === 'templates')
      this.activeTab.set(value);
  }

  loadInteractions(): void {
    this.interactionsLoading.set(true);
    this.interactionsError.set(null);
    this.comms.listInteractions({
      partyId: this.filterPartyId ?? undefined,
      channel: this.filterChannel ?? undefined,
      status: this.filterStatus ?? undefined
    }).subscribe({
      next: rows => {
        this.interactions.set(rows);
        this.interactionsLoading.set(false);
      },
      error: () => {
        this.interactionsError.set(this.t('growth.comms.interactions.errorLoad'));
        this.interactionsLoading.set(false);
      }
    });
  }

  loadDeliverability(): void {
    this.comms.getDeliverability({}).subscribe({
      next: rows => {
        this.deliverability.set(rows);
        const cards = ['Sent', 'Delivered', 'Failed', 'Bounced'].map(status => ({
          key: status,
          label: this.t(`growth.comms.deliverability.status.${status.toLowerCase()}`),
          count: rows.filter(item => item.status === status).reduce((sum, item) => sum + item.count, 0)
        }));
        this.deliverabilityCards.set(cards);
      }
    });
  }

  loadTemplates(): void {
    this.comms.listTemplates().subscribe({ next: rows => this.templates.set(rows) });
  }

  openTemplateDialog(): void {
    this.editingTemplateId = null;
    this.templateForm = { templateKey: '', channelKey: 'email', purposeKey: 'marketing', subjectTemplate: '', bodyTemplate: '', status: 'Draft' };
    this.templateVariables.set([createTemplateVariableRow()]);
    this.templateDialogVisible = true;
  }

  editTemplate(row: MessageTemplateResponse): void {
    this.editingTemplateId = row.id;
    this.templateForm = {
      templateKey: row.templateKey,
      channelKey: row.channelKey,
      purposeKey: row.purposeKey,
      subjectTemplate: row.subjectTemplate ?? '',
      bodyTemplate: row.bodyTemplate,
      status: row.status
    };
    this.templateVariables.set(parseTemplateVariables(JSON.stringify(row.variables ?? [])));
    this.templateDialogVisible = true;
  }

  addVariable(): void {
    this.templateVariables.update(rows => [...rows, createTemplateVariableRow()]);
  }

  removeVariable(localId: string): void {
    this.templateVariables.update(rows => rows.filter(item => item.localId !== localId));
  }

  saveTemplate(): void {
    const validationError = validateTemplateVariables(this.templateVariables());
    if (validationError) {
      this.interactionsError.set(this.t(validationError));
      return;
    }
    const payload = {
      subjectTemplate: this.templateForm.subjectTemplate || null,
      bodyTemplate: this.templateForm.bodyTemplate,
      status: this.templateForm.status,
      variables: this.templateVariables()
    };
    const request$ = this.editingTemplateId
      ? this.comms.updateTemplate(this.editingTemplateId, payload)
      : this.comms.createTemplate({
          templateKey: this.templateForm.templateKey,
          channelKey: this.templateForm.channelKey,
          purposeKey: this.templateForm.purposeKey,
          ...payload
        });
    request$.subscribe({
      next: () => {
        this.templateDialogVisible = false;
        this.loadTemplates();
      }
    });
  }
}
